import './applianceSpecs.scss';

import React, { useRef, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import { Remarkable } from 'remarkable';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import arrowDown from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Account/icon/arrowDown.png';
import arrowUp from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Account/icon/arrowUp.png';

export default function ApplianceSpecs(props: {
    appliance: any;
    specifications: any;
}) {
    const { appliance, specifications } = props;
    const { t } = useTranslation('pages');
    const { currentLocale } = usePageLocalization();
    const isUSStore = currentLocale === 'en_US';
    const currentDevice = useMobileDetect();
    const [showAllGeneralSpecs, setShowAllGeneralSpecs] =
        useState<boolean>(false);
    const generalSpecTitle = useRef<HTMLHeadingElement>(null);
    const specificationMap = new Map();
    specifications?.nodes.forEach((spec: any, index: number) => {
        specificationMap.set(spec?.code.replace('specification_notes_', ''), {
            options: spec?.attributes?.specification_notes,
            index,
        });
    });
    const textndIds = new Map();
    let noteIndex = 1;
    Array.from(specificationMap).forEach((spec: any) => {
        spec[1]?.options?.values.forEach((text: any) => {
            if (!textndIds.get(text?.id) && isPresentInValues(spec)) {
                textndIds.set(text?.id, { text, index: noteIndex });
                noteIndex++;
            }
        });
    });

    function isPresentInValues(spec: any) {
        if (spec[0] === 'appliance_performance') {
            return true;
        }

        let presents = Object.entries(appliance.attributes).filter(
            (attribute: any) => {
                return (
                    attribute[1] &&
                    attribute[1]?.group === 'Appliance performance'
                );
            }
        );
        return presents.find((e) => e[0] === spec[0]);
    }

    function compareAttributesIndex(a: any, b: any) {
        if (a[1].index < b[1].index) {
            return -1;
        }
        if (a[1].index > b[1].index) {
            return 1;
        }
        return 0;
    }

    const md = new Remarkable('commonmark', { breaks: true });
    const category_name = appliance?.category.split('/')[3];

    const allProducts = useStaticQuery(
        graphql`
            query {
                clearanceHeader: allProductsJson(
                    filter: { code: { eq: "occ_clearance-header" } }
                ) {
                    nodes {
                        code
                        attributes {
                            content {
                                values {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                                valuesMarkdownRemark {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                            }
                        }
                    }
                }
                clearanceFooter: allProductsJson(
                    filter: { code: { eq: "occ_clearance-footer" } }
                ) {
                    nodes {
                        code
                        attributes {
                            content {
                                values {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                                valuesMarkdownRemark {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                            }
                        }
                    }
                }
            }
        `
    );

    const clearanceHeader =
        allProducts.clearanceHeader.nodes[0]?.attributes?.content
            ?.valuesMarkdownRemark[0][currentLocale];
    const clearanceFooter =
        allProducts.clearanceFooter.nodes[0]?.attributes?.content
            ?.valuesMarkdownRemark[0][currentLocale];

    const clearanceArray: any = Object.entries(appliance.attributes).filter(
        (attribute: any) => {
            return attribute[1] && attribute[1].group === 'Degagements';
        }
    );

    function stoveTableRow(attribute: any, index: number) {
        return (
            <tr key={`stovetablerow-${index}`}>
                <th className='clearance-title'>
                    {`${t('appliance.clearance')} - ${
                        clearanceArray[index][1].name[currentLocale]
                    }${attribute[0] === 'DesSCA' ? '**' : ''}`}
                </th>
                {isUSStore ? (
                    <>
                        <td>
                            {
                                clearanceArray[index + 2][1].values[0][
                                    currentLocale
                                ]
                            }
                        </td>
                        <td>
                            {
                                clearanceArray[index + 3][1].values[0][
                                    currentLocale
                                ]
                            }
                        </td>
                    </>
                ) : (
                    <>
                        <td>
                            {clearanceArray[index][1].values[0][currentLocale]}
                        </td>
                        <td>
                            {
                                clearanceArray[index + 1][1].values[0][
                                    currentLocale
                                ]
                            }
                        </td>
                    </>
                )}
            </tr>
        );
    }

    function insertTableRow(attribute: any, index: number) {
        return (
            <tr key={`tablerow-${index}`}>
                <th className='clearance-title'>
                    {`${t('appliance.clearance')} - ${
                        clearanceArray[index][1].name[currentLocale]
                    }${
                        attribute[0] === 'DesSCA' || attribute[0] === 'DesDCA'
                            ? '**'
                            : ''
                    }`}
                </th>
                {isUSStore ? (
                    <td colSpan={2}>
                        {clearanceArray[index + 1][1].values[0].fr_CA}
                    </td>
                ) : (
                    <td colSpan={2}>
                        {clearanceArray[index][1].values[0][currentLocale]}
                    </td>
                )}
            </tr>
        );
    }

    function renderGeneralSpecs() {
        let attributes = Object.entries(appliance.attributes);

        if (currentDevice.isMobile() && !showAllGeneralSpecs) {
            attributes = attributes.slice(0, 15);
        }

        return (
            <div className='appliance-specs specs-general'>
                <h4 ref={generalSpecTitle}>
                    {t('appliance.general-specs-title')}
                </h4>
                <table>
                    <tbody>
                        {attributes
                            .filter((attribute: any) => {
                                return (
                                    attribute[1] &&
                                    attribute[1].group === 'General features'
                                );
                            })
                            .sort(compareAttributesIndex)
                            .map((attribute: any, index) => {
                                return (
                                    <tr key={`trgeneral-${index}`}>
                                        {attribute[1].name && (
                                            <th>
                                                {
                                                    attribute[1].name[
                                                        currentLocale
                                                    ]
                                                }
                                            </th>
                                        )}
                                        {attribute[1].values && (
                                            <td>
                                                {
                                                    attribute[1].values[0][
                                                        currentLocale
                                                    ]
                                                }
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
                {currentDevice.isMobile() && (
                    <div
                        className='see-more'
                        onClick={(e) => {
                            setShowAllGeneralSpecs((showMore) => !showMore);
                            generalSpecTitle.current?.scrollIntoView(true);
                            e.stopPropagation();
                        }}
                    >
                        <span>
                            {showAllGeneralSpecs ? (
                                <>
                                    {t('appliance.see-less')}{' '}
                                    <img src={arrowUp} />
                                </>
                            ) : (
                                <>
                                    {t('appliance.see-more')}{' '}
                                    <img src={arrowDown} />
                                </>
                            )}
                        </span>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className='specs'>
            <div className='left'>
                <div className='appliance-specs'>
                    <h4>
                        {t('appliance.performance-title')}
                        {createTip('appliance_performance')}
                    </h4>
                    <table>
                        <tbody>
                            {Object.entries(appliance.attributes)
                                .filter((attribute: any) => {
                                    return (
                                        attribute[1] &&
                                        attribute[1].group ===
                                            'Appliance performance'
                                    );
                                })
                                .sort(compareAttributesIndex)
                                .map((attribute: any, index: number) => {
                                    const key = `trperformance-${index}`;
                                    return (
                                        <tr key={key}>
                                            {attribute[1].name && (
                                                <th>
                                                    {
                                                        attribute[1].name[
                                                            currentLocale
                                                        ]
                                                    }
                                                    {createTip(
                                                        attribute[0],
                                                        key
                                                    )}
                                                </th>
                                            )}
                                            {attribute[1].values && (
                                                <td>
                                                    {
                                                        attribute[1].values[0][
                                                            currentLocale
                                                        ]
                                                    }
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>

                {currentDevice.isMobile() && renderGeneralSpecs()}

                <div className='appliance-specs specs-clearance'>
                    <h4>{t('appliance.clearance')}</h4>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: clearanceHeader,
                        }}
                    />

                    <table>
                        <tbody>
                            <tr>
                                <th></th>
                                {isUSStore ? (
                                    <td colSpan={2} className='country'>
                                        {t('appliance.usa')}
                                    </td>
                                ) : (
                                    <td colSpan={2} className='country'>
                                        {t('appliance.canada')}
                                    </td>
                                )}
                            </tr>

                            {category_name === 'Wood Stoves' && (
                                <tr>
                                    <th></th>
                                    <td className='pipe'>
                                        {t('appliance.simple-pipe')}
                                    </td>
                                    <td className='pipe'>
                                        {t('appliance.double-pipe')}
                                    </td>
                                </tr>
                            )}

                            {category_name === 'Wood Stoves'
                                ? clearanceArray.map(
                                      (attribute: any, index: number) => {
                                          return (
                                              index % 4 === 0 &&
                                              stoveTableRow(attribute, index)
                                          );
                                      }
                                  )
                                : clearanceArray.map(
                                      (attribute: any, index: number) => {
                                          return (
                                              index % 2 === 0 &&
                                              insertTableRow(attribute, index)
                                          );
                                      }
                                  )}

                            <tr>
                                <td className='empty'></td>
                            </tr>
                            <tr className='footnote'>
                                <td colSpan={5} className='clearance-footer'>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: clearanceFooter,
                                        }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {!currentDevice.isMobile() && renderGeneralSpecs()}
        </div>
    );

    function createTip(specCode: string, parentKey?: string): JSX.Element {
        const specification = specificationMap.get(specCode);
        if (specification) {
            let linkedSpecifications: any[] = [];
            const valuesArr = specification?.options?.values || [];
            valuesArr.forEach((value: any) => {
                const specification = textndIds.get(value.id);
                linkedSpecifications.push({ ...specification });
            });
            return (
                <span className='note-indicator'>
                    {linkedSpecifications.map((spec: any, index: number) => {
                        const tooltipRef = `${parentKey}-tooltip-${index}`;
                        return (
                            <React.Fragment key={`${parentKey}-${index}`}>
                                <span className='notetxt'>
                                    {' '}
                                    (
                                    <span data-tip data-for={tooltipRef}>
                                        {spec?.index}
                                    </span>
                                    )
                                </span>
                                <ReactTooltip
                                    id={tooltipRef}
                                    className='tooltip'
                                    place='top'
                                    type='dark'
                                    effect='solid'
                                    clickable={true}
                                >
                                    <p>{spec?.text[currentLocale]}</p>
                                </ReactTooltip>
                            </React.Fragment>
                        );
                    })}
                </span>
            );
        } else return <></>;
    }
}
